import { Dialog, DialogTitle, Button, DialogContent, DialogActions, CircularProgress, Backdrop } from "@mui/material";
import { useStore } from "../../store/store";
import { useEffect, useState } from "react";
import { convertBmpToJpeg } from "../../utils/imageUtils";
import { EegDocument, ReportData } from "./EegDocument";
import { EegDocument as EegDocumentLegacy, ReportData as ReportDataLegacy } from "./EegDocument_legacy";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import { useFileUploadToField } from "../../api/useFileUploadToField";
import { useDeleteFile } from "../../api/useDeleteFile";
import { differenceInYears, format } from "date-fns";
import { pt } from "date-fns/locale";
import { useField } from "../../api/useField";
import { useUpdateRecord } from "../../api/useUpdateRecord";
import { useUser } from "../../api/useAuth";
import { useRecords } from "../../api/useRecords";
import { makeClinicInfo } from "../../utils/makeClinicInfo";
import { makeTechnicianInfo } from "../../utils/makeTechnicianInfo";
import { FIRST_EEG_SERVICE, NFB_SERVICE, PSY_SERVICE, STALE_TIME_24H } from "../../utils/constants";
import { getIntakeFormTypeForTreatment } from "../../screens/treatments/intake-form/SelectIntakeForm";
import { findLastFormsBeforeDoneReportSession } from "../../screens/treatments/intake-form/IntakeForm";
import { findField } from "../../utils/findField";
import { getChipLabel } from "../chips/CustomChip";
import { replaceUserForSignatures } from "../pdf/replaceUserForSignatures";
import { isDateBefore } from "../../utils/dateUtils";

export const EvaluationReportDialog = () => {
    const { evaluation_report_dialog, notifications, refetchFiles } = useStore();
    const [isLoading, setIsLoading] = useState(false);
    const { data: user_technician } = useUser();

    const uploadFile = useFileUploadToField({
        field: "eeg_report",
        // @ts-ignore
        settings: {
            entity: "evaluations",
            interface: "file",
        },
    });

    const clinicsResponse = useRecords("clinics", "id,name,color,moloni_serie,receptionist.phone,receptionist.first_name,receptionist.last_name,recepcionist.*", {
        extraOptions: {
            filter: {
                id: { _neq: 2 }
            }
        },
        fetch: { fetchOnFocus: false },
        staleTime: STALE_TIME_24H,
    })
    const clinics = makeClinicInfo(clinicsResponse?.data?.items)

    const updateRecord = useUpdateRecord();
    const deleteFile = useDeleteFile();
    const { formatedField: mainProfessionalTitleField } = useField("technicians", "main_professional_title");
    const data = evaluation_report_dialog.context;
    const report = data?.item;

    const client = report?.treatment?.client;
    const user = client?.user_id;
    const eegDate = report?.eeg_session?.start_date

    const evaluationDate = eegDate
        ? format(new Date(report?.eeg_session?.start_date), "yyyy-MM-dd HH:mm")
        : "";
    const age = client?.birthday ? differenceInYears(new Date(), new Date(client.birthday)).toString() : "";

    const evaluationReferenceDate = eegDate || report?.date_created || null;
    const ageAtEvaluation = client?.birthday && evaluationReferenceDate ? differenceInYears(new Date(evaluationReferenceDate), new Date(client.birthday)) : null;
    const isD2ChildVersion = ageAtEvaluation !== null && ageAtEvaluation >= 5 && ageAtEvaluation <= 7;
    const d2Title = isD2ChildVersion ? "Cancelamento 2 Sinais" : "D2 (Teste de Atenção)";
    const d2TestListLabel = isD2ChildVersion ? "Cancelamento 2 Sinais" : "D2 - Teste de Atenção";
	const isFirstEvaluation = report?.eeg_session?.service?.id === FIRST_EEG_SERVICE

    const clinicName = client?.clinic_id?.name || "";
    const signatureLocationDate = `${clinicName}, ${format(new Date(), "d 'de' MMMM 'de' yyyy", { locale: pt })}.`

    const [convertedImagesEo, setConvertedImagesEo] = useState<string[]>([]);
    const [convertedImagesEc, setConvertedImagesEc] = useState<string[]>([]);
    const [convertedImagesOther, setConvertedImagesOther] = useState<string[]>([]);
    const [convertedImagesCbcl, setConvertedImagesCbcl] = useState<string[]>([]);
    const [convertedImagesD2, setConvertedImagesD2] = useState<string[]>([]);
    const [convertedImagesBdi, setConvertedImagesBdi] = useState<string[]>([]);
    const [convertedImagesBsi, setConvertedImagesBsi] = useState<string[]>([]);
    const [convertedImagesAsrs, setConvertedImagesAsrs] = useState<string[]>([]);
    const [correctedReportSessionUser, setCorrectedReportSessionUser] = useState<any>(null);

    const handleSaveReport = async () => {
        notifications.setNotification({
            isOpen: false,
        });

        setIsLoading(true);

        try {
            const blob = await pdf(documentElement).toBlob();
            const eegDate = report?.eeg_session?.start_date ? format(new Date(report?.eeg_session?.start_date), "yyyyMMdd") : "";
            const file = new File([blob], `EEG Quantitativo ${user?.first_name} ${user?.last_name} ${eegDate}.pdf`, {
                type: "application/pdf",
            });

            // Delete previous file
            if (report?.eeg_report) {
                await deleteFile.mutateAsync({
                    id: report?.eeg_report?.id || report?.eeg_report,
                    entity: "evaluations",
                });
            }

            // Upload new file
            await uploadFile.mutateAsync({
                file: file,
                entity: "evaluations",
                folder: "a09a0214-e0ee-46f2-a0b4-0c863836ba9e",
                itemId: report?.id,
            });

            // Update user as the one who did the evaluation
            await updateRecord.mutateAsync({
                id: report?.id,
                entity: "evaluations",
                data: {
                    //@ts-ignore
                    evaluation_done_by: user_technician?.technician_id[0]?.id,
                    report_created_date: new Date().toISOString(),
                },
            });

            notifications.setNotification({
                isOpen: true,
                message: `Relatório de avaliação guardado com sucesso`,
                severity: "success",
            });

            refetchFiles.setTimestamp(Date.now());
            evaluation_report_dialog.setIsOpen(false);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errorMessage || error.message || "Erro ao guardar relatório de avaliação";
            notifications.setNotification({
                isOpen: true,
                message: errorMessage,
                severity: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const processData = async () => {
            setConvertedImagesEo([]);
            setConvertedImagesEc([]);
            setConvertedImagesOther([]);
            setConvertedImagesCbcl([]);
            setConvertedImagesD2([]);
            setConvertedImagesBdi([]);
            setConvertedImagesBsi([]);
            setConvertedImagesAsrs([]);
            setCorrectedReportSessionUser(null);

            if (data?.item) {
                const convertImageSet = async (images: any[], rotation = true) => {
                    return await Promise.all(images.map(async (img: any) => convertBmpToJpeg(img.directus_files_id, rotation)));
                };

                if (data.item.eo_images.length > 0) {
                    setConvertedImagesEo(await convertImageSet(data.item.eo_images));
                }
                if (data.item.ec_images.length > 0) {
                    setConvertedImagesEc(await convertImageSet(data.item.ec_images));
                }
                if (data.item.other_images.length > 0) {
                    setConvertedImagesOther(await convertImageSet(data.item.other_images));
                }
                if (data.item.cbcl_images?.length > 0) {
                    setConvertedImagesCbcl(await convertImageSet(data.item.cbcl_images, false));
                }
                if (data.item.d2_images?.length > 0) {
                    setConvertedImagesD2(await convertImageSet(data.item.d2_images, false));
                }
                if (data.item.bdi_images?.length > 0) {
                    setConvertedImagesBdi(await convertImageSet(data.item.bdi_images, false));
                }
                if (data.item.bsi_images?.length > 0) {
                    setConvertedImagesBsi(await convertImageSet(data.item.bsi_images, false));
                }
                if (data.item.asrs_images?.length > 0) {
                    setConvertedImagesAsrs(await convertImageSet(data.item.asrs_images, false));
                }

                const reportSessionUser = data.item.report_session?.technician?.user_id;
                if (reportSessionUser) {
                    const correctedUser = await replaceUserForSignatures(reportSessionUser);
                    setCorrectedReportSessionUser(correctedUser);
                }
            }
        };
        processData();
    }, [data?.item]);

    if (evaluation_report_dialog.isInstrumentsVersion) return null;

    const formatedFields = data?.formatedFields
    const readyToOpen = report && user_technician && formatedFields
    if (!readyToOpen)
        return evaluation_report_dialog.isOpen
            ? <Backdrop open={true}><CircularProgress color="primary" size={50} /></Backdrop>
            : null;

    const brainMapsTechnicianUser = report?.brain_maps_technician?.user_id;
    const technician = brainMapsTechnicianUser ? makeTechnicianInfo(brainMapsTechnicianUser, mainProfessionalTitleField) : null;

    const treatment = report?.treatment;
    const prescriber = correctedReportSessionUser ? makeTechnicianInfo(correctedReportSessionUser, mainProfessionalTitleField) : null;

    const laterality = getClientLaterality(treatment, report);

    const doneSessionsText = makeSessionCount(treatment?.sessions || [], eegDate);

    const colaborationText = makeCollaboration(formatedFields, report);

    const reportData: ReportData = {
        patientName: `${user?.first_name} ${user?.last_name}`,
        birthDate: client?.birthday || "",
        age: age || "",
        evaluationDate: evaluationDate,
        medication: report?.medication || "",
        laterality: laterality,
        evaluationMotivation: report?.evaluation_motivation || "",
        medicalPsychologicalHistory: report?.medical_psychological_history || "",
        doneSessions: doneSessionsText,
        therapeuticGoals: report?.therapeutic_goals || "",
        evaluationsTests: [
            "Anamnese",
            ...(convertedImagesCbcl.length > 0 ? ["CBCL - Child Behavior Checklist"] : []),
            ...(convertedImagesD2.length > 0 ? [d2TestListLabel] : []),
            ...(convertedImagesAsrs.length > 0 ? ["ASRS-18 - Escala de auto-avaliação de PHDA para o adulto"] : []),
            ...(convertedImagesBsi.length > 0 ? ["BSI - Inventário de Sintomas Psicopatológicos"] : []),
            ...(convertedImagesBdi.length > 0 ? ["BDI - Inventário Depressivo de Beck"] : []),
        ],
        colaboration: colaborationText,
        resultsInterpertation: report?.content_evaluation,
        eyesOpen: {
            incidenciaAbsoluta: report?.eo_absolute_incidence || "",
            conectividade: report?.eo_conectivity || "",
            images: convertedImagesEo,
        },
        eyesClosed: {
            incidenciaAbsoluta: report?.ec_absolute_incidence || "",
            conectividade: report?.ec_conectivity || "",
            images: convertedImagesEc,
        },
        observations: report?.observations || "",
        signatureLocationDate: signatureLocationDate,
        technician: technician,
        prescriberTechnician: prescriber,
        otherImages: convertedImagesOther,
        cbclImages: convertedImagesCbcl,
        d2Images: convertedImagesD2,
        d2Title: d2Title,
        bdiImages: convertedImagesBdi,
        bsiImages: convertedImagesBsi,
        asrsImages: convertedImagesAsrs,
        clinics: clinics,
        isFirstEvaluation: isFirstEvaluation
    };

    const legacyReportData: ReportDataLegacy = {
        patientName: reportData.patientName,
        birthDate: reportData.birthDate,
        age: reportData.age,
        evaluationDate: reportData.evaluationDate,
        medication: reportData.medication,
        laterality: reportData.laterality,
        evaluationMotivation: reportData.evaluationMotivation,
        medicalPsychologicalHistory: reportData.medicalPsychologicalHistory,
        doneSessions: reportData.doneSessions,
        therapeuticGoals: reportData.therapeuticGoals,
        evaluationsTests: reportData.evaluationsTests.join(", "),
        colaboration: reportData.colaboration,
        resultsInterpertation: reportData.resultsInterpertation,
        eyesOpen: reportData.eyesOpen,
        eyesClosed: reportData.eyesClosed,
        observations: reportData.observations,
        technician: reportData.technician,
        otherImages: reportData.otherImages,
        clinics: reportData.clinics,
        isFirstEvaluation: reportData.isFirstEvaluation,
    };

    const documentElement = evaluation_report_dialog.isLegacy
        ? <EegDocumentLegacy reportData={legacyReportData} />
        : <EegDocument reportData={reportData} />;

    return (
        <Dialog
            open={evaluation_report_dialog.isOpen}
            onClose={() => evaluation_report_dialog.setIsOpen(false)}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>Relatório de Avaliação</DialogTitle>

            <DialogContent style={{ display: "flex", justifyContent: "center" }}>
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <PDFViewer width="1000" height="1200">
                        {documentElement}
                    </PDFViewer>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={() => evaluation_report_dialog.setIsOpen(false)}>
                    Cancelar
                </Button>
                <Button onClick={handleSaveReport}>
                    {isLoading ? <CircularProgress color="inherit" size={24} /> : "Guardar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const makeSessionCount = (sessionsAll: Array<any>, cutOffDate: string) => {
	const sessions = sessionsAll.filter((s: any) => isDateBefore(s.start_date, cutOffDate))

    const doneNfbCount = sessions.filter((s: any) => s.service?.id === NFB_SERVICE && s.status === "done").length;
    const donePsyCount = sessions.filter((s: any) => s.service?.id === PSY_SERVICE && s.status === "done").length;
    const doneSessionsParts = [];
    if (doneNfbCount > 0) doneSessionsParts.push(`${doneNfbCount} sessões de Neurofeedback`);
    if (donePsyCount > 0) doneSessionsParts.push(`${donePsyCount} sessões de Psicologia Clínica`);
    const doneSessionsText = doneSessionsParts.length > 0 ? doneSessionsParts.join(", ") + "." : "";
    return doneSessionsText;
};

const makeCollaboration = (formatedFields: Array<any>, report: any) => {
    const clientCollaborationField = findField(formatedFields, "client_collaboration_new");
    const clientCollaborationNoteField = findField(formatedFields, "client_collaboration_new_note");
    const clientCollaboration = getChipLabel(clientCollaborationField, report?.client_collaboration_new);
    const clientCollaborationNote = (report?.client_collaboration_new_note || []).map((item: string) => getChipLabel(clientCollaborationNoteField, item)?.toLowerCase()).join(", ");
    const colaborationText = clientCollaboration ? `${clientCollaboration}${clientCollaborationNote ? `, ${clientCollaborationNote}` : ""}.` : "";
    return colaborationText;
};

const getClientLaterality = (treatment: any, report: any) => {
    const intakeFormType = getIntakeFormTypeForTreatment(treatment, report?.report_session?.end_date);
    const intakeForms = intakeFormType === "intake_forms_child" ? treatment?.intake_form_child : treatment?.intake_form_adult;
    const selectedIntakeForm = findLastFormsBeforeDoneReportSession(intakeForms, report?.report_session?.end_date);
    const laterality = selectedIntakeForm?.laterality || "";
    return laterality;
};
